import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import { motion } from "framer-motion"; // Import framer-motion
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../store";
import { GetTournamentList } from "../store/user/actions";
import { TournamentCardView } from "../components/TournamentCardView";
import { TournamentListView } from "../components/TournamentListView";
import { TournamentResultView } from "../components/TournamentResultView";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import "../pages/Tools.css"; // Reuse the wood-themed styling

const uri_prefix = "https://ipfs.io/ipfs/";

export const Tournament = () => {
  const dispatch = useDispatch<any>();
  const [tournamentId, setTournamentId] = useState(0);
  const [showInstructions, setShowInstructions] = useState(false);

  const userModule = useSelector((state: AppState) => state.userModule);
  const { loginInfo, gameStatus } = userModule;

  useEffect(() => {
    dispatch(GetTournamentList());
  }, [dispatch]);

  // Show loading while authentication is in progress
  if (gameStatus.loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 2, md: 4 } }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          flexDirection: 'column',
          gap: 2
        }}>
          <img src="/images/earth_rot.gif" alt="Loading..." style={{ width: "100px" }} />
          <Typography variant="h6" color="white">
            Loading Tournament Data...
          </Typography>
        </Box>
      </Container>
    );
  }

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const slideIn = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.5 } },
  };

  const instructionsContent = (
    <motion.div
      key="fadeInAnimation"
      initial="hidden"
      animate="visible"
      variants={fadeIn}
    >
      <Paper
        className="game-paper"
        sx={{
          p: 4,
          mb: 3,
          position: "relative",
          zIndex: 1,
          background: "linear-gradient(135deg, #5c3c10 0%, #4a300b 100%)",
          border: "8px solid #5c3c10",
          borderRadius: "16px",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.3) 0px, rgba(92, 60, 16, 0.3) 2px, transparent 2px, transparent 4px)",
            opacity: 0.5,
            zIndex: 0,
          },
          "& > *": {
            position: "relative",
            zIndex: 1,
          },
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: "#ffffff",
            fontWeight: "bold",
            textAlign: "center",
            textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
          }}
        >
          How Tournaments Work
        </Typography>
        <Divider sx={{ mb: 3, bgcolor: "rgba(255, 255, 255, 0.2)" }} />
        <Typography
          variant="body1"
          paragraph
          sx={{ color: "#ffffff", textAlign: "left" }}
        >
          Compete with your Deer NFTs to win exciting prizes:
        </Typography>
        <ol
          style={{
            paddingLeft: "1.5rem",
            color: "#ffffff",
            textAlign: "left",
          }}
        >
          <li>
            <Typography variant="body1" paragraph sx={{ color: "#ffffff" }}>
              Enter a tournament by selecting your Deer NFT(s).
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph sx={{ color: "#ffffff" }}>
              Each tournament has an entry fee in ANTLERS.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph sx={{ color: "#ffffff" }}>
              Winners receive ANTLERS prizes and special NFT rewards.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph sx={{ color: "#ffffff" }}>
              Tournament results are publicly viewable.
            </Typography>
          </li>
        </ol>
        <Box
          sx={{
            bgcolor: "rgba(217, 83, 79, 0.1)",
            p: 2,
            borderRadius: "8px",
            border: "1px solid rgba(217, 83, 79, 0.3)",
            mb: 3,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ color: "#ffffff", fontWeight: "bold" }}
          >
            Pro Tip:
          </Typography>
          <Typography variant="body2" sx={{ color: "#ffffff" }}>
            Higher-level Deer have better stats and increased chances of winning
            tournaments. Select your strongest Deer for the best results!
          </Typography>
        </Box>
        <Box sx={{ textAlign: "center" }}>
            <Button
            variant="contained"
            onClick={() => {
              setShowInstructions(false);
            }}
            sx={{
              bgcolor: "#3d2c0a",
              color: "white",
              "&:hover": { bgcolor: "#5c3c10" },
            }}
            component={motion.button}
            whileTap={{ scale: 0.95 }}
            >
            Got it!
            </Button>
        </Box>
      </Paper>
    </motion.div>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 2, md: 4 } }}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={slideIn}
      >
        <Paper
          elevation={3}
          sx={{
            p: { xs: 1, sm: 2, md: 3 },
            mb: 4,
            background: "linear-gradient(135deg, #4a300b 0%, #3d2c0a 100%)",
            borderRadius: "16px",
            position: "relative",
            overflow: "hidden",
            border: "8px solid #5c3c10",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 2, sm: 0 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="h4"
                fontWeight="bold"
                color="white"
                sx={{ textShadow: "1px 1px px rgba(0,0,0,0.6)" }}
              >
                🏆TOURNAMENTS ARENA
              </Typography>
              <Typography
                variant="body1"
                color="rgba(255,255,255,0.9)"
                sx={{
                  mt: 1,
                  textShadow: "1px 1px 2px rgba(0,0,0,0.8)",
                }}
              >
                Compete with your Deer in tournaments to win epic prizes
              </Typography>
            </Box>

            <Tooltip title="View Instructions">
              <IconButton
                onClick={() => setShowInstructions(!showInstructions)}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                }}
                size="large"
              >
                <HelpOutlineIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.2) 0px, rgba(92, 60, 16, 0.2) 2px, transparent 2px, transparent 4px)",
              opacity: 0.4,
              zIndex: 1,
              pointerEvents: "none",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              right: { xs: -30, md: 50 },
              top: 0,
              opacity: 0.15,
              zIndex: 1,
              transform: "rotate(-10deg)",
            }}
          >
            <EmojiEventsIcon sx={{ fontSize: { xs: 80, md: 150 } }} />
          </Box>
        </Paper>
      </motion.div>
      {showInstructions ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.3 }}
        >
          {instructionsContent}
        </motion.div>
      ) : (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          <Paper
            elevation={3}
            className="wood-panel"
            sx={{
              p: { xs: 1, sm: 2, md: 3 },
              borderRadius: "16px",
              position: "relative",
              background: "linear-gradient(135deg, #5c3c10 0%, #4a300b 100%)",
              border: "8px solid #5c3c10",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "repeating-linear-gradient(45deg, rgba(92, 60, 16, 0.3) 0px, rgba(92, 60, 16, 0.3) 2px, transparent 2px, transparent 4px)",
                opacity: 0.5,
                zIndex: 0,
              },
              "& > *": {
                position: "relative",
                zIndex: 1,
              },
            }}
          >
            {tournamentId !== 0 && (
              <Box sx={{ textAlign: "left", width: "100%", mb: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setTournamentId(0)}
                  sx={{
                    backgroundColor: "#2eca6a",
                    color: "white",
                    "&:hover": { backgroundColor: "#25a65a" },
                    borderRadius: "8px",
                    px: 3,
                    py: 1,
                  }}
                >
                  Back to Tournaments
                </Button>
              </Box>
            )}
            {tournamentId === 0 && <TournamentListView setId={setTournamentId} />}
            {tournamentId > 0 && <TournamentCardView id={tournamentId} back={() => setTournamentId(0)} />}
            {tournamentId < 0 && <TournamentResultView id={tournamentId} />}
          </Paper>
        </motion.div>
      )}
    </Container>
  );
};

export default Tournament;